// Theme system types for flexible tenant configuration

export interface ColorScheme {
  primary: string
  secondary: string
  accent: string
  background: string
  text: string
  border: string
  success: string
  warning: string
  danger: string
  info: string
}

export interface BrandingConfig {
  siteName: string
  logoUrl: string
  faviconUrl: string
  footerText?: string
}

export interface ThemeAssets {
  baseTheme: 'classic' | 'modern'  // Layout structure
  colorScheme: ColorScheme
  branding: BrandingConfig
  customCSS?: string  // Additional custom CSS if needed
}

export interface TenantThemeConfig {
  tenantId: string
  themeId: string  // Theme identifier for CSS loading
  theme: ThemeAssets
  settings: {
    marqueeText: string
    welcomeMessage?: string
    contactInfo?: {
      line?: string
      lineUrl?: string
      phone?: string
      email?: string
    }
    socialLinks?: {
      facebook?: string
      instagram?: string
      twitter?: string
    }
    rulesAndRegulations?: {
      type: 'text' | 'image'
      textContent: string
      imageUrl: string
    }
    paymentRates?: {
      type: 'text' | 'image'
      textContent: string
      imageUrl: string
    }
    /** เปิด/ปิด Web Chat ในหน้า user (เมื่อปิดจะซ่อนไอคอนแชท) */
    webChatEnabled?: boolean
    /** เปิดการฝากเงินทั้งระบบ — false = ปิดหน้า/ช่องทางฝากทันที */
    depositEnabled?: boolean
    /** เปิดการถอนเงินทั้งระบบ — false = ปิดหน้า/ช่องทางถอน */
    withdrawEnabled?: boolean
    /** เปิดการเข้าเล่นหวยทั้งระบบ — false = ปิดปรับปรุงระบบหวย (บล็อกแทงหวย/ยี่กี/ชุด) */
    lotteryEnabled?: boolean
    /** เปิดการเข้าเล่นเกมส์ กีฬา คาสิโนทั้งระบบ — false = ปิดปรับปรุงระบบเกมส์ */
    casinoEnabled?: boolean
    /** นาทีที่เหลือก่อนปิดรับ — แสดงผลวิบวับ (หวยยี่กี), 1–120, default 15 */
    wipWupMinutes?: number
    /** นาทีที่เหลือก่อนปิดรับ — แสดงผลวิบวับ (หวยอื่นๆ ในรายการหวยวันนี้), 1–120, default 15 */
    wipWupMinutesOther?: number
    /** Idle logout: นาทีที่ไม่ได้ใช้งานแล้ว logout อัตโนมัติ (0 = ปิด, 1–120 = นาที), default 30. มาจาก features.sessionIdleMinutes */
    sessionIdleMinutes?: number
    /** ช่องถ่ายทอดสดอิสระ (บอล/กีฬา) */
    liveTvChannels?: Array<{
      id: string
      title: string
      youtubeUrl: string
      enabled: boolean
      category?: 'football' | 'lottery' | 'other'
      sortOrder?: number
    }>
    /** เปิดหน้า Live TV และปุ่มลอย (default true เมื่อมีช่อง active) */
    liveTvEnabled?: boolean
    predictionEnabled?: boolean
    /** YouTube Live ต่อหวย (key = huayCode uppercased / huayId) — global ตั้งจาก /owner-live-stream */
    huayYoutubeLive?: Record<string, {
      youtubeUrl: string
      enabled: boolean
      label?: string
      updatedAt?: string
    }>
    /** Global player default from /owner-live-stream — true = start muted on autoplay */
    liveTvPlayerStartMuted?: boolean
  }
}

// Pre-defined color schemes for easy setup
export const COLOR_SCHEMES: Record<string, ColorScheme> = {
  'asia-green': {
    primary: '#28a745',
    secondary: '#17a2b8',
    accent: '#ffc107',
    background: '#f8f9fa',
    text: '#212529',
    border: '#dee2e6',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8'
  },
  'fast-blue': {
    primary: '#007bff',
    secondary: '#6c757d',
    accent: '#17a2b8',
    background: '#f8f9fa',
    text: '#212529',
    border: '#dee2e6',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8'
  },
  'today-modern-blue': {
    primary: '#1e3a8a',
    secondary: '#1e40af',
    accent: '#3b82f6',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    text: '#ffffff',
    border: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  },
  'today-modern-red': {
    primary: '#dc2626',
    secondary: '#991b1b',
    accent: '#ef4444',
    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
    text: '#ffffff',
    border: '#ef4444',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  },
  'today-modern-lightblue': {
    primary: '#0ea5e9',
    secondary: '#0284c7',
    accent: '#38bdf8',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    text: '#ffffff',
    border: '#38bdf8',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4'
  }
}
